import dayjs from "dayjs";
import React, { Ref, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetCountryListByPage } from "../../../../../../api/services/common/commonApis";
import {
  useGetAllProgramTemplateForDropdown,
  useGetLineOfBusiness,
} from "../../../../../../api/services/programTemplate/programTemplateApis";
import {
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiSelectWithTextInput,
} from "../../../../../../components";
import {
  LANGUAGE_CODE,
  MAX_LENGTH_75,
  PAGE_NAME_FOR_COUNTRY_DROPDOWN,
  PAGE_OPERATION_TYPE,
} from "../../../../../../helpers/constants";
import {
  getValueOrBlank,
  setApiDataToDropDownOption,
} from "../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { SearchParticipantFormKey } from "../../../../modelsAndSchemas/Models";
import { bulkActivitySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { useMultiSelectARIALabels } from "../../../../../../hooks/useMultiSelectARIALabels";

export interface SearchParticipantsFormProps {
  pageId: string;
  programTemplateRef: Ref<{ resetValues: () => void }>;
}

export const SearchParticipantsForm: React.FC<SearchParticipantsFormProps> = ({
  pageId,
  programTemplateRef,
}) => {
  const dispatch = useDispatch();
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();

  const { control, getValues } = useFormContext();
  const { commonLabels, fetchLabel, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const {
    data: countryListData,
    isError: isCountryError,
    isFetching: isCountryFetching,
  } = useGetCountryListByPage(
    PAGE_NAME_FOR_COUNTRY_DROPDOWN.BULK_PROCESS,
    PAGE_OPERATION_TYPE.ADD,
  );

  const {
    data: lineOfBusinessData,
    isError: isLineOfBusinessError,
    isFetching: isBusinessFetching,
  } = useGetLineOfBusiness(LANGUAGE_CODE.EN_US);

  const {
    data: programTemplateList,
    isError: isProgramError,
    isFetching: isProgramFetching,
  } = useGetAllProgramTemplateForDropdown();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isCountryFetching || isBusinessFetching || isProgramFetching,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    };
  }, [isCountryFetching, isBusinessFetching, isProgramFetching]);

  useEffect(() => {
    if (isCountryError || isLineOfBusinessError || isProgramError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
        }),
      );
    }
  }, [isCountryError, isLineOfBusinessError]);

  const getSearchLabel = (key: string) => {
    return fetchLabel(bulkActivitySiteCoreKeys.searchParticipantsKey, key);
  };

  const filterDropdownValue = fetchDropdownValues(
    bulkActivitySiteCoreKeys.searchParticipantsKey,
    bulkActivitySiteCoreKeys.filterByList,
  );

  return (
    <MuiBox>
      <MuiGrid
        container
        className="form-container"
        data-testid="search-participant-form"
      >
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={SearchParticipantFormKey.participantName}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getSearchLabel(bulkActivitySiteCoreKeys.participantName)}
              placeholder={commonLabels.inputPlaceHolder}
              control={control}
              selectBoxName={SearchParticipantFormKey.participantNameFilter}
              id={`${pageId}-${SearchParticipantFormKey.participantName}`}
              selectBoxId={`${pageId}-${SearchParticipantFormKey.participantNameFilter}`}
              maxLength={MAX_LENGTH_75}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={SearchParticipantFormKey.programName}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getSearchLabel(bulkActivitySiteCoreKeys.programName)}
              placeholder={commonLabels.inputPlaceHolder}
              control={control}
              selectBoxName={SearchParticipantFormKey.programNameFilter}
              id={`${pageId}-${SearchParticipantFormKey.programName}`}
              selectBoxId={`${pageId}-${SearchParticipantFormKey.programNameFilter}`}
              maxLength={MAX_LENGTH_75}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={SearchParticipantFormKey.clientName}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getSearchLabel(bulkActivitySiteCoreKeys.client)}
              placeholder={commonLabels.inputPlaceHolder}
              control={control}
              selectBoxName={SearchParticipantFormKey.clientNameFilter}
              id={`${pageId}-${SearchParticipantFormKey.clientName}`}
              selectBoxId={`${pageId}-${SearchParticipantFormKey.clientNameFilter}`}
              maxLength={MAX_LENGTH_75}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={SearchParticipantFormKey.productLine}
          >
            <MuiSelect
              label={getSearchLabel(bulkActivitySiteCoreKeys.productLine)}
              options={setApiDataToDropDownOption(lineOfBusinessData)}
              value=""
              visibleDefaultSelectOption
              showSearch
              id={`${pageId}-${SearchParticipantFormKey.productLine}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={SearchParticipantFormKey.programTemplate}
            ref={programTemplateRef}
          >
            <MuiMultiSelect
              label={getSearchLabel(bulkActivitySiteCoreKeys.programTemplate)}
              options={setApiDataToDropDownOption(programTemplateList)}
              showSearch
              showChip
              id={`${pageId}-${SearchParticipantFormKey.programTemplate}`}
              chipId={`${pageId}-${SearchParticipantFormKey.programTemplate}-chip`}
              dialogTitle={getSearchLabel(
                bulkActivitySiteCoreKeys.programTemplate,
              )}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={SearchParticipantFormKey.status}
          >
            <MuiSelect
              label={getSearchLabel(bulkActivitySiteCoreKeys.status)}
              options={fetchDropdownValues(
                bulkActivitySiteCoreKeys.searchParticipantsKey,
                bulkActivitySiteCoreKeys.statusList,
              )}
              value=""
              visibleDefaultSelectOption
              id={`${pageId}-${SearchParticipantFormKey.status}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={SearchParticipantFormKey.deliveryCountry}
          >
            <MuiSelect
              label={getSearchLabel(bulkActivitySiteCoreKeys.deliveryCountry)}
              options={setApiDataToDropDownOption(countryListData, true)}
              value=""
              visibleDefaultSelectOption
              id={`${pageId}-${SearchParticipantFormKey.deliveryCountry}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              showSearch
              searchPlaceholder={commonLabels.search}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={SearchParticipantFormKey.deliveryOffice}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getSearchLabel(bulkActivitySiteCoreKeys.deliveryOffice)}
              placeholder={commonLabels.inputPlaceHolder}
              control={control}
              selectBoxName={SearchParticipantFormKey.deliveryOfficeFilter}
              id={`${pageId}-${SearchParticipantFormKey.deliveryOffice}`}
              selectBoxId={`${pageId}-${SearchParticipantFormKey.deliveryOfficeFilter}`}
              maxLength={MAX_LENGTH_75}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={SearchParticipantFormKey.coach}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getSearchLabel(bulkActivitySiteCoreKeys.coach)}
              placeholder={commonLabels.inputPlaceHolder}
              control={control}
              selectBoxName={SearchParticipantFormKey.coachFilter}
              id={`${pageId}-${SearchParticipantFormKey.coach}`}
              selectBoxId={`${pageId}-${SearchParticipantFormKey.coachFilter}`}
              maxLength={MAX_LENGTH_75}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <MuiDateFieldFormController
            control={control}
            name={SearchParticipantFormKey.createdDate}
            value={
              getValues(SearchParticipantFormKey.createdDate) !== ""
                ? dayjs(
                    getValues(SearchParticipantFormKey.createdDate),
                    userCountryDateFormat,
                  )
                : null
            }
            id={`${pageId}-${SearchParticipantFormKey.createdDate}`}
            label={
              getSearchLabel(bulkActivitySiteCoreKeys.createdDate)
            }
            className="date-picker"
            disableManualInput
            valueFormat={userCountryDateFormat}
            format={userCountryDateFormat}
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
          />
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
